import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Radio, ArrowLeft, AlertCircle } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function EventStream() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const code = params.get("code");
  const { user, loading } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) {
      setError("Please sign in to access this stream");
      return;
    }
    if (!code) {
      setError("Missing access code. Use the link from your dashboard.");
      return;
    }
    api.get(`/events/${id}/stream`, { params: { code } })
      .then((r) => setData(r.data))
      .catch((err) => setError(err.response?.data?.detail || "Access denied"));
  }, [id, code, user, loading]);

  if (error) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center bg-[#F5F5F5] py-20">
        <div className="max-w-md text-center bg-white p-10 border-t-4 border-[#C00000]">
          <AlertCircle className="w-12 h-12 text-[#C00000] mx-auto mb-5" />
          <h1 className="font-display text-3xl text-[#1F3864] mb-3">Stream Unavailable</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <Link to="/dashboard" className="btn-primary-red">
            <ArrowLeft className="w-4 h-4" /> Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  if (!data) {
    return <div className="py-32 text-center text-gray-500">Verifying access...</div>;
  }

  const embed = data.stream_embed_url;
  return (
    <div className="bg-[#0B1526] text-white min-h-[80vh]">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to Dashboard
        </Link>
        <div className="text-xs tracking-[0.3em] uppercase text-[#C00000] mb-3 flex items-center gap-2">
          <Radio className="w-4 h-4" /> Live Stream Access
        </div>
        <h1 className="font-display text-4xl sm:text-5xl mb-4">{data.event.title}</h1>
        <p className="text-white/60 mb-8">{data.event.venue} · {new Date(data.event.event_date).toLocaleString("en-ZA")}</p>

        <div className="aspect-video bg-black video-embed border border-white/10">
          {embed ? (
            <iframe
              title={data.event.title}
              src={embed}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
              allowFullScreen
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center text-center p-8">
              <Radio className="w-12 h-12 text-[#C00000] mb-4" />
              <h3 className="font-display text-2xl mb-2">Stream not yet live</h3>
              <p className="text-white/60 max-w-md">
                Your access is confirmed. The organisers will publish the stream URL shortly before the event begins.
                Check back on this page closer to the event time.
              </p>
            </div>
          )}
        </div>

        <div className="mt-8 p-5 border border-white/10 bg-white/5 flex flex-wrap justify-between gap-4 text-sm">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-white/50 mb-1">Access Code</div>
            <code className="font-mono tracking-widest text-[#C00000]">{data.access_code}</code>
          </div>
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-white/50 mb-1">Expires</div>
            <div>{new Date(data.expires_at).toLocaleString("en-ZA")}</div>
          </div>
        </div>
      </section>
    </div>
  );
}
